import { MOTIONS, motionOf, type Animation, type Direction, type Ease, type ExitAnimation, type LayerAnimation, type LoopAnimation, type MotionType, type TextBy } from '@flobi/artwork-core';

/**
 * What the Animate bar decides, kept out of React so it can be tested without a page.
 *
 * The bar is recipes for the whole page, and — once a layer is picked — three questions
 * about that layer: how it comes in, what it does while it is there and how it goes out.
 * Which moves are offered, which few controls a move uses, how the Slow–Fast slider maps
 * onto seconds, how an edit is written and how a move is tried before it is chosen all
 * live here, so the component only draws.
 */

export type Phase = 'in' | 'loop' | 'out';
export type Scope = 'page' | 'layer';
/** An animation either of them can wear. */
export type AnyAnimation = Animation | LayerAnimation;
/** A preset: a motion, or none at all. */
export type PresetValue = MotionType | 'none';

export type Exit = ExitAnimation;
export type Loop = LoopAnimation;
export type { TextBy };
/**
 * The newer fields, read loosely: a page written by an older build reaches the panel
 * without them (the scene skips zod), and one still saying `when` says it the old way.
 */
type Extras = { exit?: Exit; loop?: Loop; when?: 'enter' | 'exit' | 'both'; textBy?: TextBy; textStagger?: number };
const extras = (anim: AnyAnimation | null): Extras => (anim ?? {}) as Extras;

// ── speed ────────────────────────────────────────────────────────────────────

/**
 * The seconds a move takes when nothing says otherwise. Arriving and leaving share one
 * scale; a loop's span is one whole cycle, which wants a longer one — a 0.6 s breath is a pant.
 */
export function usualSpan(phase: Phase): number {
  return phase === 'loop' ? 1.6 : 0.6;
}

/**
 * The Slow–Fast slider: 0 is the slowest it offers and 100 the fastest, in seconds a move
 * takes (a loop: one cycle). No seconds on screen — somebody making a post thinks "a bit
 * quicker", not "0.45 s" — but every stop is a twentieth of a second, so it lands on tidy
 * numbers a recipe or the timeline can also say.
 */
const SLIDER: Readonly<Record<'move' | 'loop', { readonly slow: number; readonly fast: number }>> = {
  move: { slow: 1.6, fast: 0.25 },
  loop: { slow: 3, fast: 0.7 },
};
const sliderOf = (phase: Phase) => (phase === 'loop' ? SLIDER.loop : SLIDER.move);

/** Where the slider sits for a span; one a recipe set outside its ends sits at that end. */
export function speedValue(phase: Phase, span: number): number {
  const { slow, fast } = sliderOf(phase);
  return Math.round(Math.max(0, Math.min(1, (slow - span) / (slow - fast))) * 100);
}

/** The seconds a slider position stands for. */
export function spanAt(phase: Phase, value: number): number {
  const { slow, fast } = sliderOf(phase);
  const span = slow - (Math.max(0, Math.min(100, value)) / 100) * (slow - fast);
  return Math.round(span * 20) / 20;
}

// ── a recipe's gear ──────────────────────────────────────────────────────────

/**
 * A recipe's pace on its Slow–Fast slider. The middle is the recipe as written; each end
 * is two and a half times slower or quicker — far enough to change its character, not so
 * far that it stops being it. Even steps of the slider are even RATIOS of pace, so the
 * first nudge either way is felt as much as the last.
 */
const PACE_REACH = 2.5;
export function paceValue(speed: number | undefined): number {
  return Math.round(Math.max(0, Math.min(100, 50 + (Math.log(speed ?? 1) / Math.log(PACE_REACH)) * 50)));
}
export function paceAt(value: number): number {
  const at = Math.max(0, Math.min(100, value));
  // Held to the recipe's own pace near the middle, so "as it was" can be found again.
  if (Math.abs(at - 50) <= 2) return 1;
  return Math.round(PACE_REACH ** ((at - 50) / 50) * 100) / 100;
}

/**
 * How far apart a recipe's things set off, on its Together–Apart slider: the left end is
 * all at once, the middle as written, the right end `most` times the gap — the engine's
 * `spreadMost`, which is as many times as it takes for that recipe to read as apart.
 */
export function spreadValue(spread: number | undefined, most = 3): number {
  const gap = spread ?? 1;
  return Math.round(Math.max(0, Math.min(100, gap <= 1 ? gap * 50 : 50 + ((gap - 1) / Math.max(0.001, most - 1)) * 50)));
}
export function spreadAt(value: number, most = 3): number {
  const at = Math.max(0, Math.min(100, value));
  if (Math.abs(at - 50) <= 2) return 1;
  return Math.round((at <= 50 ? at / 50 : 1 + ((at - 50) / 50) * (most - 1)) * 100) / 100;
}

/** How text arrives under a recipe, as its gear offers it. */
export const TEXT_BY_NAMES: readonly (readonly [TextBy, string])[] = [
  ['whole', 'At Once'],
  ['word', 'By Word'],
  ['letter', 'By Letter'],
];

// ── directions ───────────────────────────────────────────────────────────────

/** The pad, row by row; null is its middle. Each is the way the layer travels to arrive. */
export const PAD: readonly (Direction | null)[] = ['upLeft', 'up', 'upRight', 'left', null, 'right', 'downLeft', 'down', 'downRight'];

export const DIRECTION_NAMES: Readonly<Record<string, string>> = {
  up: 'Up',
  down: 'Down',
  left: 'Left',
  right: 'Right',
  upLeft: 'Up and Left',
  upRight: 'Up and Right',
  downLeft: 'Down and Left',
  downRight: 'Down and Right',
};

/** Which way a direction points, as a unit-ish vector on screen (y down). */
export function headingOf(direction: string): { x: number; y: number } {
  const x = direction.toLowerCase().includes('left') ? -1 : direction.toLowerCase().includes('right') ? 1 : 0;
  const y = direction.startsWith('up') ? -1 : direction.startsWith('down') ? 1 : 0;
  return { x, y };
}

/** The other way round: up and left becomes down and right. */
export function oppositeOf(direction: string): string {
  const swap: Record<string, string> = { up: 'down', down: 'up', left: 'right', right: 'left', upLeft: 'downRight', downRight: 'upLeft', upRight: 'downLeft', downLeft: 'upRight' };
  return swap[direction] ?? direction;
}

/** The angle an arrow is turned to point along a direction, from pointing up. */
export function arrowAngle(direction: string): number {
  const { x, y } = headingOf(direction);
  return Math.round((Math.atan2(x, -y) * 180) / Math.PI);
}

// ── presets ──────────────────────────────────────────────────────────────────

/** The three questions, in the words somebody making a post would use. */
export const TABS: readonly (readonly [Phase, string])[] = [
  ['in', 'Comes In'],
  ['loop', 'Keeps Moving'],
  ['out', 'Goes Out'],
];

export interface Preset {
  readonly value: PresetValue;
  readonly name: string;
  readonly note: string;
}

const NONE_NOTES: Record<Phase, string> = {
  in: 'Already there when the page starts',
  loop: 'Holds still while it is on the page',
  out: 'Stays until the page ends',
};

/** Leaving is not arriving backwards in words: a Rise that leaves is a Lift. */
const EXIT_NAMES: Partial<Record<string, readonly [string, string]>> = {
  rise: ['Lift', 'Rises away and fades'],
  fade: ['Fade', 'Fades where it is'],
  pop: ['Shrink', 'Shrinks away'],
  pan: ['Pan', 'Slides off, keeping its weight'],
  slide: ['Slide', 'Slides out of the page'],
  wipe: ['Wipe', 'Covers itself up'],
  reveal: ['Conceal', 'Slides away behind its own edge'],
  stretch: ['Squeeze', 'Closes up to a line'],
  roll: ['Roll', 'Rolls away'],
  zoom: ['Zoom', 'Grows as it fades'],
  blur: ['Blur', 'Blurs away'],
  drop: ['Fall', 'Drops out of the page'],
  flip: ['Flip', 'Flips away'],
  spin: ['Spin', 'Turns as it leaves'],
};

/** What a tab calls a preset: a Rise that leaves is a Lift. */
export function presetName(phase: Phase, value: PresetValue): string {
  if (value === 'none') return 'None';
  if (phase === 'out' && EXIT_NAMES[value]) return EXIT_NAMES[value]![0];
  return motionOf(value)?.name ?? value;
}

export interface PresetContext {
  readonly scope: Scope;
  /** The chosen layer's type, when the scope is a layer. */
  readonly layerType?: string | null;
  /** What is chosen now, which always stays in the list so it shows. */
  readonly current?: PresetValue | null;
}

/** The tiles for one tab: None, then its motions. */
export function presetsFor(phase: Phase, ctx: PresetContext): Preset[] {
  const text = ctx.scope === 'layer' && ctx.layerType === 'text';
  const out: Preset[] = [{ value: 'none', name: 'None', note: NONE_NOTES[phase] }];
  for (const one of MOTIONS) {
    if ((one.type as string) === 'none') continue;
    const kept = one.type === ctx.current;
    if (phase === 'loop') {
      if (!one.loops) continue;
      out.push({ value: one.type, name: one.name, note: one.note });
      continue;
    }
    if (one.loops) continue;
    if (phase === 'out') {
      // A path ends somewhere else and a typewriter only types: neither is a way to leave.
      if ((one.drawn || one.typed) && !kept) continue;
      const named = EXIT_NAMES[one.type];
      out.push({ value: one.type, name: named?.[0] ?? one.name, note: named?.[1] ?? one.note });
      continue;
    }
    // A page-wide path would be every layer on one line; a typewriter means nothing on a
    // photograph. What is chosen always stays, so it shows (anim-F15).
    if (one.drawn && ctx.scope !== 'layer' && !kept) continue;
    if (one.typed && !text && !kept) continue;
    out.push({ value: one.type, name: one.name, note: one.note });
  }
  return out;
}

// ── which controls ───────────────────────────────────────────────────────────

export interface Controls {
  /** Which way it travels: only a move that travels has one. */
  readonly direction: boolean;
  /** The Slow–Fast slider. */
  readonly speed: boolean;
  /** Word by Word. */
  readonly textBy: boolean;
  /** Draw the Path. */
  readonly path: boolean;
}

const NOTHING: Controls = { direction: false, speed: false, textBy: false, path: false };

/**
 * Only what the chosen move uses, and never more than these four. A Pop has nowhere to
 * come from, and words one at a time only means something on text. How strong a move is
 * and how it eases are the recipe's or the move's own: they stay as set, and are not asked.
 */
export function controlsFor(phase: Phase, motion: PresetValue | null, ctx: { layerType?: string | null }): Controls {
  if (!motion || motion === 'none') return NOTHING;
  const kind = motionOf(motion);
  if (phase === 'loop') return { ...NOTHING, speed: true };
  const travels = !!kind?.travels && !kind?.drawn;
  if (phase === 'out') return { ...NOTHING, direction: travels, speed: true };
  return {
    direction: travels,
    speed: true,
    // Only motions that move, turn, scale, fade or blur can go a word at a time; a wipe
    // and a typewriter already say how text is uncovered.
    textBy: ctx.layerType === 'text' && !!kind?.splits,
    path: !!kind?.drawn,
  };
}

// ── reading an animation ─────────────────────────────────────────────────────

const loops = (motion: string | undefined) => !!(motion && motionOf(motion)?.loops);

/**
 * The preset each tab shows as chosen, or null when there is no animation to show.
 *
 * Older animations say the same things differently — a loop motion as the main motion,
 * or `when: 'exit'` — and are read into the three tabs so nothing set is hidden.
 */
export function presetOf(anim: AnyAnimation | null, phase: Phase): PresetValue | null {
  if (!anim) return null;
  const x = extras(anim);
  const when = x.when ?? 'enter';
  if (phase === 'loop') return x.loop?.motion ?? (loops(anim.motion) ? anim.motion : 'none');
  if (phase === 'out') {
    if (x.exit) return x.exit.motion;
    return when !== 'enter' && !loops(anim.motion) ? anim.motion : 'none';
  }
  if (loops(anim.motion) || when === 'exit') return 'none';
  return anim.motion;
}

/** The numbers a tab's controls show. */
export function phaseValues(anim: AnyAnimation, phase: Phase): { span: number; ease: Ease; direction: Direction; intensity: number } {
  const n = normalise(anim);
  const x = extras(n);
  if (phase === 'loop') return { span: x.loop?.span ?? usualSpan('loop'), ease: n.ease, direction: n.direction, intensity: x.loop?.intensity ?? 1 };
  if (phase === 'out' && x.exit) return { span: x.exit.span, ease: x.exit.ease, direction: x.exit.direction, intensity: x.exit.intensity };
  return { span: n.span, ease: n.ease, direction: n.direction, intensity: n.intensity };
}

/**
 * The same animation said the new way: a loop in `loop`, a leaving in `exit`, and the
 * main motion only ever the entrance. Applied before an edit, never on read.
 */
export function normalise<T extends AnyAnimation>(anim: T): T {
  const x = extras(anim);
  let next: T & Extras = { ...anim } as T & Extras;
  const when = x.when ?? 'enter';
  if (loops(anim.motion)) {
    next = { ...next, loop: x.loop ?? { motion: anim.motion, span: anim.span, intensity: anim.intensity }, motion: 'none' as MotionType };
  } else if (when !== 'enter') {
    next = { ...next, exit: x.exit ?? { motion: anim.motion, span: anim.span, ease: anim.ease, direction: anim.direction, intensity: anim.intensity } };
    if (when === 'exit') next = { ...next, motion: 'none' as MotionType };
  }
  if (x.when !== undefined && x.when !== 'enter') next = { ...next, when: 'enter' };
  return next;
}

/** Drops an optional field rather than writing it as undefined. */
function without<T extends object>(value: T, key: string): T {
  const copy = { ...value } as Record<string, unknown>;
  delete copy[key];
  return copy as T;
}

// ── writing an animation ─────────────────────────────────────────────────────

/**
 * An animation with this tab's preset chosen.
 *
 * `make` builds a fresh animation (the engine's defaults) when there is none. Choosing
 * keeps whatever timing is set, so trying presets in turn is a comparison, not a reset.
 */
export function withPreset<T extends AnyAnimation>(anim: T | null, phase: Phase, value: PresetValue, make: (motion: MotionType) => T): T {
  const base = normalise(anim ?? make('none' as MotionType));
  const x = extras(base);
  if (phase === 'in') return { ...base, motion: value as MotionType };
  if (phase === 'loop') {
    if (value === 'none') return without(base, 'loop');
    const loop: Loop = { motion: value, span: x.loop?.span ?? usualSpan('loop'), intensity: x.loop?.intensity ?? 1 };
    return { ...base, loop } as T;
  }
  if (value === 'none') return without(base, 'exit');
  const exit: Exit = {
    motion: value,
    span: x.exit?.span ?? usualSpan('out'),
    // Eased at both ends, the engine's own default for leaving: a layer that sprang in
    // should not bounce out.
    ease: x.exit?.ease ?? 'inOut',
    // The way it travels to leave: carrying on the way it came reads as a movement, backing
    // out the way it came reads as an undo.
    direction: x.exit?.direction ?? base.direction,
    intensity: x.exit?.intensity ?? 1,
  };
  return { ...base, exit } as T;
}

export type PhaseProps = Partial<{ span: number; ease: Ease; direction: Direction; intensity: number }>;

/** One tab's numbers changed; everything else kept. */
export function withPhase<T extends AnyAnimation>(anim: T, phase: Phase, props: PhaseProps): T {
  const base = normalise(anim);
  const x = extras(base);
  if (phase === 'in') return { ...base, ...props };
  if (phase === 'loop') {
    if (!x.loop) return base;
    const { span, intensity } = props;
    return { ...base, loop: { ...x.loop, ...(span !== undefined ? { span } : {}), ...(intensity !== undefined ? { intensity } : {}) } } as T;
  }
  if (!x.exit) return base;
  return { ...base, exit: { ...x.exit, ...props } } as T;
}

/** Whether anything at all is set: an entrance, a loop or an exit. */
export function isAnimated(anim: AnyAnimation | null): boolean {
  if (!anim) return false;
  return presetOf(anim, 'in') !== 'none' || presetOf(anim, 'loop') !== 'none' || presetOf(anim, 'out') !== 'none';
}

// ── text ─────────────────────────────────────────────────────────────────────

export function textByOf(anim: AnyAnimation | null): TextBy {
  return extras(anim).textBy ?? 'whole';
}
/** Seconds between one word or letter setting off and the next. */
export function textStaggerOf(anim: AnyAnimation | null): number {
  const gap = extras(anim).textStagger;
  return typeof gap === 'number' ? gap : 0.04;
}

// ── trying and watching ──────────────────────────────────────────────────────

/** A wait before a tried arrival, so the eye sees the layer gone before it comes in. */
const TRY_WAIT = 0.15;

/**
 * A move as it is TRIED on one layer, before it is chosen: only the tab being looked at
 * plays, so a Float is judged as a Float and not as the tail of an entrance. `hold` is how
 * long it then stays arrived — a beat to look at where it landed, a second cycle of a
 * loop, a moment standing before it leaves. Null for None: there is nothing to watch.
 */
export function tryOut(base: LayerAnimation | null, phase: Phase, value: PresetValue, make: (motion: MotionType) => LayerAnimation): { animation: LayerAnimation; hold: number } | null {
  if (value === 'none') return null;
  const next = withPreset(base, phase, value, make);
  const x = extras(next);
  if (phase === 'in') return { animation: { ...without(without(next, 'loop'), 'exit'), delay: TRY_WAIT }, hold: 0.8 };
  const still = { ...next, motion: 'none' as MotionType, delay: 0 };
  if (phase === 'loop') return { animation: without(still, 'exit'), hold: x.loop?.span ?? usualSpan('loop') };
  return { animation: without(still, 'loop'), hold: 0.6 };
}

/** When a layer is on the page and when it arrives and leaves: a row of the timeline, or the engine's schedule. */
type Moments = { readonly window: { from: number; until: number }; readonly enter: { from: number; until: number } | null; readonly exit: { from: number; until: number } | null };

/**
 * Where to play the page from to watch what was just changed: a breath before the layer
 * comes in, before it starts to keep moving, or before it goes — not from nought, which on
 * a page of ten layers is eight seconds of waiting to see one change.
 */
export function playFrom(phase: Phase, moments: Moments | null | undefined): number {
  if (!moments) return 0;
  const at = phase === 'out' ? (moments.exit?.from ?? moments.window.until) - 0.6 : phase === 'loop' ? (moments.enter?.until ?? moments.window.from) - 0.2 : (moments.enter?.from ?? moments.window.from) - 0.2;
  return Math.max(0, Math.round(at * 100) / 100);
}

// ── recipes ──────────────────────────────────────────────────────────────────

export const MOODS = 'Moods';

/**
 * The recipes in groups, in the order the engine lists them — except Moods, the nine
 * older styles, which go last: they are the general-purpose ones, and the named recipes
 * are what somebody opening the panel is most likely to want.
 */
export function recipeGroups<T extends { readonly group?: string }>(styles: readonly T[]): { title: string; items: T[] }[] {
  const groups: { title: string; items: T[] }[] = [];
  for (const style of styles) {
    const title = style.group ?? MOODS;
    let group = groups.find((one) => one.title === title);
    if (!group) groups.push((group = { title, items: [] }));
    group.items.push(style);
  }
  return [...groups.filter((one) => one.title !== MOODS), ...groups.filter((one) => one.title === MOODS)];
}

// ── thumbnails ───────────────────────────────────────────────────────────────

/** CSS easing curves standing in for the engine's, for the little moving pictures only. */
export const CSS_EASE: Readonly<Record<string, string>> = {
  linear: 'linear',
  out: 'cubic-bezier(.33,1,.68,1)',
  inOut: 'cubic-bezier(.65,0,.35,1)',
  back: 'cubic-bezier(.34,1.56,.64,1)',
  smooth: 'cubic-bezier(.22,1,.36,1)',
  snappy: 'cubic-bezier(.2,.9,.05,1)',
  spring: 'cubic-bezier(.34,1.56,.64,1)',
  bounce: 'cubic-bezier(.5,2.1,.45,.75)',
};

/**
 * Where a thumbnail's shape starts from for an arriving motion, as CSS custom properties
 * the shared `anim-thumb-*` keyframes read. `reach` is the distance of a full-strength
 * travel, in pixels of the thumbnail.
 */
export function enterVars(motion: string, direction: string, amount = 1, reach = 14): Record<string, string | number> {
  const h = headingOf(direction);
  const d = reach * amount;
  const px = (n: number) => `${Math.round(n * 10) / 10}px`;
  switch (motion) {
    case 'rise':
      return { '--tx': px(-h.x * d), '--ty': px(-h.y * d), '--o0': 0 };
    case 'fade':
      return { '--o0': 0 };
    case 'pop':
      return { '--sc': Math.max(0.1, 1 - 0.5 * amount), '--o0': 0 };
    case 'pan':
    case 'slide':
      return { '--tx': px(-h.x * d * 2.4), '--ty': px(-h.y * d * 2.4) };
    case 'wipe': {
      const cp = h.x > 0 ? 'inset(0 100% 0 0)' : h.x < 0 ? 'inset(0 0 0 100%)' : h.y < 0 ? 'inset(100% 0 0 0)' : 'inset(0 0 100% 0)';
      return { '--cp': cp };
    }
    case 'reveal': {
      // One whole box away, behind a window that stays where it rests: the clip is the
      // shape's own and moves with it, so it opens from the far edge at the same rate.
      const cp = h.y < 0 ? 'inset(0 0 100% 0)' : h.y > 0 ? 'inset(100% 0 0 0)' : h.x < 0 ? 'inset(0 100% 0 0)' : 'inset(0 0 0 100%)';
      return { '--tx': `${-Math.sign(h.x) * 100}%`, '--ty': `${-Math.sign(h.y) * 100}%`, '--cp': cp };
    }
    case 'stretch':
      return { '--sx': Math.max(0.04, 1 - 0.96 * Math.min(1, amount)) };
    case 'roll':
      return { '--tx': px(-h.x * d * 2), '--ty': px(-h.y * d * 2), '--rot': `${Math.round((h.x < 0 ? 180 : -180) * amount)}deg`, '--o0': 0 };
    case 'zoom':
      return { '--sc': 1 + 0.6 * amount, '--o0': 0 };
    case 'blur':
      return { '--bl': px(4 * amount), '--o0': 0 };
    case 'drop':
      return { '--ty': px(-d * 2.2), '--o0': 1 };
    case 'flip':
      return { '--rx': `${Math.round(90 * Math.min(1, amount))}deg`, '--o0': 0 };
    case 'spin':
      return { '--rot': `${Math.round(-200 * amount)}deg`, '--sc': 0.5, '--o0': 0 };
    case 'typewriter':
      return { '--cp': 'inset(0 100% 0 0)' };
    case 'path':
      return { '--tx': px(-d * 1.6), '--ty': px(d * 0.8) };
    default:
      return {};
  }
}

/** A loop's swing, as properties for the `anim-thumb-loop` keyframes, and its cycle in seconds. */
export function loopVars(motion: string, amount = 1): { vars: Record<string, string | number>; cycle: number; spin?: boolean; held?: boolean } {
  switch (motion) {
    case 'sway':
      return { vars: { '--lx': `${4 * amount}px` }, cycle: 1.6 };
    case 'orbit':
      return { vars: { '--lx': `${3 * amount}px`, '--ly': `${-3 * amount}px` }, cycle: 1.2 };
    case 'jitter':
      return { vars: { '--lx': `${1.2 * amount}px`, '--ly': `${-1 * amount}px`, '--lr': `${5 * amount}deg` }, cycle: 0.36, held: true };
    case 'blink':
      return { vars: { '--lo': 0.2 }, cycle: 0.5, held: true };
    case 'float':
      return { vars: { '--ly': `${-4 * amount}px` }, cycle: 1.6 };
    case 'pulse':
      return { vars: { '--ls': 1 + 0.12 * amount }, cycle: 0.7 };
    case 'wiggle':
      return { vars: { '--lr': `${8 * amount}deg` }, cycle: 0.3 };
    case 'shake':
      return { vars: { '--lx': `${3 * amount}px` }, cycle: 0.1 };
    case 'breathe':
      return { vars: { '--lo': 0.55, '--ls': 1 + 0.06 * amount }, cycle: 1.8 };
    case 'bob':
      return { vars: { '--ly': `${3 * amount}px`, '--lr': `${4 * amount}deg` }, cycle: 0.9 };
    case 'turn':
      return { vars: {}, cycle: 2.4, spin: true };
    default:
      return { vars: {}, cycle: 1 };
  }
}

/**
 * How a move's tile stands while nothing points at it: a dashed outline where the layer
 * starts from (or, leaving, ends up) and the layer itself where it rests — so ten tiles
 * of the same layer are ten different pictures. `ghost` is the outline's transform, or
 * null for none; `picture` is what a move with nowhere to come from does to the layer's
 * own picture instead (a fade is faint, a wipe half uncovered).
 */
export function restLook(phase: Phase, motion: PresetValue, direction: string): { ghost: string | null; picture: Record<string, string | number> } {
  if (motion === 'none') return { ghost: null, picture: {} };
  if (phase === 'loop') {
    const { vars, spin } = loopVars(motion, 1.6);
    if (spin) return { ghost: 'rotate(40deg)', picture: {} };
    const parts = [
      vars['--lx'] !== undefined || vars['--ly'] !== undefined ? `translate(${vars['--lx'] ?? '0px'}, ${vars['--ly'] ?? '0px'})` : '',
      vars['--ls'] !== undefined ? `scale(${vars['--ls']})` : '',
      vars['--lr'] !== undefined ? `rotate(${vars['--lr']})` : '',
    ].filter(Boolean);
    if (parts.length > 0) return { ghost: parts.join(' '), picture: {} };
    return { ghost: null, picture: vars['--lo'] !== undefined ? { opacity: 0.4 } : {} };
  }
  const vars = enterVars(motion, phase === 'out' ? oppositeOf(direction) : direction, 1, 15);
  const parts = [
    vars['--tx'] !== undefined || vars['--ty'] !== undefined ? `translate(${vars['--tx'] ?? '0px'}, ${vars['--ty'] ?? '0px'})` : '',
    vars['--sc'] !== undefined ? `scale(${vars['--sc']})` : '',
    vars['--sx'] !== undefined ? `scaleX(${vars['--sx']})` : '',
    vars['--rot'] !== undefined ? `rotate(${vars['--rot']})` : '',
    vars['--rx'] !== undefined ? 'scaleY(0.3)' : '',
  ].filter(Boolean);
  if (parts.length > 0) return { ghost: parts.join(' '), picture: {} };
  // Uncovered from one side: half of it showing, and its whole outline.
  if (typeof vars['--cp'] === 'string') return { ghost: 'none', picture: { clipPath: vars['--cp'].replace('100%', '50%') } };
  if (vars['--bl'] !== undefined) return { ghost: null, picture: { filter: 'blur(1.6px)', opacity: 0.75 } };
  return { ghost: null, picture: vars['--o0'] === 0 ? { opacity: 0.4 } : {} };
}

/** One animation of a recipe, read loosely: enough to draw it, whatever else it carries. */
export type RecipePart = { motion?: string; direction?: string; ease?: string; delay?: number; span?: number; intensity?: number; textBy?: string; textStagger?: number; loop?: { motion?: string } };
/** What a recipe says: its page's animation and the parts it gives some roles. Its card is drawn from this (`recipeScenes.ts`). */
export interface RecipeLike {
  readonly page: RecipePart & { stagger?: number; order?: string };
  readonly parts?: Partial<Record<string, RecipePart>>;
  /** `words`: the texts arrive first and everything else waits for them. */
  readonly lead?: string;
}
